import { useState, useRef } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectCopyAssistEnabled } from 'core/userContext/selectors';
import { InputText } from 'ui/inputs';
import { Handlebars, NegativeFill } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { ContentModals, ContentModalStateType, useModalContext } from 'utils/contexts/modal';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { CopyAssistButtonDropdown } from 'modules/ai/copyAssist/CopyAssistButtonDropdown';
import { CopyAssistTagNames } from 'modules/domains/copyAssist/copyAssist';
import { isMessageMediumCopyAssistantSupported } from 'modules/ai/copyAssist/utils';
import { SettingsReadOnlyUrl } from '../EditorContentContainer.styled';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { TextInputFieldProps } from '../types';
import { getIsEditMode, useFieldErrorTheme } from '../utils';
import { displayContent } from '../content';
import { MESSAGE_MEDIUM_DATA_ARRAY_DELIMITER } from '../constants';

const TextWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.utils.pxToRem(3)};
  right: ${({ theme }) => theme.spacing.utils.pxToRem(-7)};
  z-index: 2;
`;

const StyledTooltip = styled(Tooltip)`
  position: absolute;
  top: ${({ theme }) => theme.spacing.utils.pxToRem(8)};
  right: ${({ theme }) => theme.spacing.utils.pxToRem(22)};
`;

const StyledInputText = styled(InputText)`
  align-items: center;
  border-width: ${({ theme, hasError }) => (hasError ? theme.spacing.utils.pxToRem(2) : '')};
`;

const StyledText = styled.div<{ $hasError: boolean }>`
  ${({ theme }) => theme.styles.body.body2};
  color: ${({ $hasError, theme }) =>
    $hasError ? theme.colors.utilityColors.negative100 : theme.colors.textColors.textPrimary};
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.values.eighth};
  word-break: break-word;
  white-space: break-spaces;
`;

const INPUT_WIDTH = '100%';
const NO_ICON = false;

export const TextInputField = ({
  isDisabled = false,
  mediumRef = null,
  mediumDataKey,
  mediumDataValue = '',
  showSnippet = true,
  changeHandler,
  placeholderText = '',
  hasError = false,
  hasWarning = false,
  maxLength,
  Suffix = null,
}: TextInputFieldProps) => {
  const { view } = useQueryParamContext();
  const { isEditMode, selectedMessageMedium, setCurrentMediumField } = useEditableFieldsContext();
  const { showModal } = useModalContext<ContentModalStateType>();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const value = Array.isArray(mediumDataValue)
    ? mediumDataValue.join(MESSAGE_MEDIUM_DATA_ARRAY_DELIMITER)
    : mediumDataValue;

  const errorTheme = useFieldErrorTheme(hasError, hasWarning);

  const textInputFieldWrapperEl = useRef<HTMLDivElement>(null);

  // Ref of wrapper required to target Copy Assist btn dynamically
  const textInputWrapperWithHandlebar = useRef<HTMLDivElement>(null);

  const isCopyAssistEnabled = useSelector(selectCopyAssistEnabled);

  const [isTextInputFieldFocused, setIsTextInputFieldFocused] = useState(false);

  const showCopyAssist =
    isTextInputFieldFocused &&
    isCopyAssistEnabled &&
    isMessageMediumCopyAssistantSupported(selectedMessageMedium, mediumDataKey);

  useClickOutside(textInputFieldWrapperEl, () => {
    setIsTextInputFieldFocused(false);
  });

  return (
    <div data-test="text-input-field">
      {isEditModeVisible ? (
        <TextWrapper
          ref={textInputFieldWrapperEl}
          onFocus={() => {
            setIsTextInputFieldFocused(true);
          }}
        >
          {showSnippet && (
            <StyledTooltip tooltipContent={displayContent.handlebarTooltip}>
              <IconWrapper
                data-test="snippet-button"
                onClick={() =>
                  showModal({
                    modal: ContentModals.InsertSnippet,
                    mediumDataKey,
                    mediumDataValue: value,
                    mediumRef,
                  })
                }
              >
                <Handlebars color={colors.purple90} />
              </IconWrapper>
            </StyledTooltip>
          )}
          <StyledInputText
            isDisabled={isDisabled}
            inputRef={mediumRef}
            inputWrapperRef={textInputWrapperWithHandlebar}
            data-test={`${mediumDataKey}-input-field`}
            id={`${mediumDataKey}-input-field`}
            onChange={changeHandler}
            value={value}
            width={INPUT_WIDTH}
            placeholder={placeholderText}
            hasError={hasError}
            errorTheme={errorTheme}
            hasIcon={NO_ICON}
            maxLength={maxLength}
            Suffix={Suffix}
          />

          {showCopyAssist && (
            <CopyAssistButtonDropdown
              targetRef={textInputWrapperWithHandlebar}
              mediumDataKey={mediumDataKey}
              value={value}
              targetTagName={CopyAssistTagNames.Input}
              setCurrentMediumField={setCurrentMediumField}
              messageMedium={selectedMessageMedium}
            />
          )}
        </TextWrapper>
      ) : (
        <>
          {value ? (
            <StyledText data-test={`${mediumDataKey}-text-ro`} $hasError={hasError}>
              {hasError && <NegativeFill color={errorTheme?.errorColor} />}
              {value}
            </StyledText>
          ) : (
            <SettingsReadOnlyUrl data-test={`${mediumDataKey}-text-ro`} isUnset={!value}>
              {displayContent.strUnsetText}
            </SettingsReadOnlyUrl>
          )}
        </>
      )}
    </div>
  );
};
